import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Center,
  Box,
  Progress,
  Image,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

function getPasswordStrength(password: string): number {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 25;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
  if (/\d/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
  return Math.min(100, strength);
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    validate: zodResolver(resetPasswordSchema),
    initialValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const passwordStrength = getPasswordStrength(form.values.password);

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await updatePassword(values.password);

      if (result.success) {
        navigate('/login');
      } else {
        setError(result.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Container size={420}>
        <Center mb="xl">
          <Image src="/logo-inverse.png" alt="TrustPuma" h={80} w="auto" />
        </Center>

        <Paper withBorder shadow="xl" p="xl" radius="md">
          <Title order={2} ta="center" mb="md">
            Reset your password
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            Enter your new password below.
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <PasswordInput
                label="New Password"
                placeholder="Enter a strong password"
                required
                {...form.getInputProps('password')}
              />

              {form.values.password && (
                <Box>
                  <Text size="xs" mb={5}>
                    Password strength
                  </Text>
                  <Progress
                    value={passwordStrength}
                    color={passwordStrength > 75 ? 'teal' : passwordStrength > 50 ? 'yellow' : 'red'}
                    size="sm"
                  />
                </Box>
              )}

              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm your new password"
                required
                {...form.getInputProps('confirmPassword')}
              />

              <Button type="submit" fullWidth mt="md" loading={isLoading}>
                Reset password
              </Button>
            </Stack>
          </form>

          <Text c="dimmed" size="sm" ta="center" mt="xl">
            Remember your password?{' '}
            <Link to="/login" style={{ fontWeight: 500 }}>
              Sign in
            </Link>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
}
